import { useCallback, useEffect, useRef } from "react";
import { useCommands } from "@components/commands/useCommands";
import {
    CommandsDirectory,
    InjectedCommandOption,
} from "@components/commands/lib";

interface UseInvoiceBulkCommandActionsParams {
    selectedCount: number;
    isBusy?: boolean;
    onMarkVoid?: () => void;
    onMarkUncollectible?: () => void;
    invoiceBulkCounterpartyOptions?: InjectedCommandOption[];
    invoiceBulkScheduleOptions?: InjectedCommandOption[];
    canChangeCounterparty?: boolean;
    onDownloadPdf?: () => void;
    canDownloadPdf?: boolean;
    downloadPdfDisabledTooltip?: string;
    onDelete?: () => void;
    canDelete?: boolean;
    onCreateTask?: () => void;
}

// Routes the invoices table's bulk-actions trigger through the shared cmd-k
// palette (CommandsDirectory.INVOICE_BULK). Status actions, Download PDF and Delete
// close the palette before doing their work. Set counterparty stays in the
// palette and drills into the same searchable counterparty list transactions use.
export function useInvoiceBulkCommandActions({
    selectedCount,
    isBusy = false,
    onMarkVoid,
    onMarkUncollectible,
    invoiceBulkCounterpartyOptions,
    invoiceBulkScheduleOptions,
    canChangeCounterparty = false,
    onDownloadPdf,
    canDownloadPdf = false,
    downloadPdfDisabledTooltip,
    onDelete,
    canDelete = false,
    onCreateTask,
}: UseInvoiceBulkCommandActionsParams) {
    const {
        open: openCommands,
        close: closeCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();

    const handleOpenBulkActions = useCallback(() => {
        if (selectedCount === 0) {
            return;
        }
        openCommands(CommandsDirectory.INVOICE_BULK, {
            selectedInvoiceCount: selectedCount,
            onInvoiceBulkMarkVoid:
                onMarkVoid && !isBusy
                    ? () => {
                          closeCommands();
                          onMarkVoid();
                      }
                    : undefined,
            onInvoiceBulkMarkUncollectible:
                onMarkUncollectible && !isBusy
                    ? () => {
                          closeCommands();
                          onMarkUncollectible();
                      }
                    : undefined,
            invoiceBulkCounterpartyOptions: isBusy
                ? undefined
                : invoiceBulkCounterpartyOptions,
            canInvoiceBulkChangeCounterparty: canChangeCounterparty,
            invoiceBulkScheduleOptions: isBusy
                ? undefined
                : invoiceBulkScheduleOptions,
            onInvoiceBulkDownloadPdf:
                onDownloadPdf && !isBusy
                    ? () => {
                          closeCommands();
                          onDownloadPdf();
                      }
                    : undefined,
            canInvoiceBulkDownloadPdf: canDownloadPdf,
            invoiceBulkDownloadPdfDisabledTooltip: downloadPdfDisabledTooltip,
            onInvoiceBulkDelete:
                onDelete && !isBusy
                    ? () => {
                          closeCommands();
                          onDelete();
                      }
                    : undefined,
            canInvoiceBulkDelete: canDelete,
            onInvoiceBulkCreateTask:
                onCreateTask && !isBusy
                    ? () => {
                          closeCommands();
                          onCreateTask();
                      }
                    : undefined,
        });
    }, [
        openCommands,
        closeCommands,
        selectedCount,
        isBusy,
        onMarkVoid,
        onMarkUncollectible,
        invoiceBulkCounterpartyOptions,
        invoiceBulkScheduleOptions,
        canChangeCounterparty,
        onDownloadPdf,
        canDownloadPdf,
        downloadPdfDisabledTooltip,
        onDelete,
        canDelete,
        onCreateTask,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (selectedCount === 0) {
            return undefined;
        }
        registerOpenOverride(() => handleOpenBulkActionsRef.current());
        return () => unregisterOpenOverride();
    }, [selectedCount, registerOpenOverride, unregisterOpenOverride]);

    return { handleOpenBulkActions };
}
